import { makeStyles, TypographyVariant, Link as MaterialLink } from "@material-ui/core";
import Typography from "@material-ui/core/Typography/Typography";
import React, { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    applicationPalettes,
    montserratFontFamily,
    primaryApplicationFontFamily,
    TxtColor,
} from "shared/theme/defaultTheme";

type LinkTypography = Exclude<TypographyVariant, "button">;
interface LinkPropTypes {
    label: React.ReactNode;
    tabIndex?: number;
    link?: string;
    onClick?: (() => void) | null;
    typographyVariant: LinkTypography;
    isOpenAtNewPage?: boolean;
    disabled?: boolean;
    download?: boolean;
    noWrap?: boolean;
    className?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reference?: React.Ref<any>;
}

const { Txt1, Txt3 } = TxtColor;

const useStyles = makeStyles({
    wrapper: {
        maxWidth: "100%",
        display: "inline-block",
    },
    customLinkType: {
        cursor: "pointer",
        textDecoration: "none",
        fontFamily: primaryApplicationFontFamily,
        maxWidth: "100%",
        "&.link1": {
            color: applicationPalettes.primary[500],
            borderBottom: "1px solid transparent",
            "&:hover": {
                color: applicationPalettes.primary[700],
                borderColor: applicationPalettes.primary[300],
            },
            "&:active": {
                color: applicationPalettes.primary[900],
            },
        },
        "&.link1subtitle": {
            fontFamily: montserratFontFamily,
            color: applicationPalettes.primary[500],
            borderBottom: "1px solid transparent",
            "&:hover": {
                color: applicationPalettes.primary[700],
                borderColor: applicationPalettes.primary[300],
            },
            "&:active": {
                color: applicationPalettes.primary[900],
            },
        },
        "&.link2": {
            color: Txt3,
            "&:hover": {
                color: applicationPalettes.primary[500],
            },
            "&:active": {
                color: applicationPalettes.primary[900],
            },
        },
        "&.link3": {
            color: Txt1,
            "&:hover": {
                color: applicationPalettes.primary[500],
            },
            "&:active": {
                color: applicationPalettes.primary[700],
            },
        },
        "&.link-button": {
            borderBottom: "1px solid transparent",
        },
    },
});

type TypographyVariantsValue = "link1" | "link1subtitle" | "link2" | "link3";

const typographyVariants: { [key in LinkTypography]: TypographyVariantsValue } = {
    body1: "link1",
    body2: "link1",
    subtitle1: "link1subtitle",
    subtitle2: "link1subtitle",
    caption: "link2",
    overline: "link2",
    h1: "link3",
    h2: "link3",
    h3: "link3",
    h4: "link3",
    h5: "link3",
    h6: "link3",
};

function getLinkVariant(typographyVariant: LinkTypography): string {
    return typographyVariants[typographyVariant];
}

/**
 * Link component
 * @param {string} label - A text content of the link
 * @param {function=} onClick - click handler (only if there is not "link" parameter)
 * @param {string=} link - path which is going to be provided to link. (only if there is not "onClick" parameter)
 * @param {string} typographyVariant - Value which changes styling for component due to typography palette and is responsible for link type changing.
 * @param {isOpenAtNewPage} boolean - Value which is open link at new page if true.
 */
function Link({
    label,
    link,
    typographyVariant,
    onClick = null,
    disabled = false,
    isOpenAtNewPage,
    tabIndex = 0,
    download,
    noWrap = false,
    reference,
    className,
}: LinkPropTypes): JSX.Element {
    const styles = useStyles();
    const isOutside =
        link && (link.indexOf("http") !== -1 || link.indexOf("tel") !== -1 || link.indexOf("mailto") !== -1);

    const content = useMemo(() => {
        if (!disabled) {
            if (onClick) {
                return (
                    <MaterialLink
                        className={`${styles.customLinkType} ${className} link-button`}
                        variant={typographyVariant}
                        onClick={onClick}
                        tabIndex={tabIndex}
                        target={isOpenAtNewPage ? "_blank" : "_self"}
                    >
                        {label}
                    </MaterialLink>
                );
            }

            if (isOutside) {
                return (
                    <MaterialLink
                        className={`${styles.customLinkType} ${className}`}
                        variant={typographyVariant}
                        href={link}
                        tabIndex={tabIndex}
                        target={isOpenAtNewPage ? "_blank" : "_self"}
                        download={download}
                    >
                        {label}
                    </MaterialLink>
                );
            }

            if (link) {
                return (
                    <RouterLink
                        className={`${styles.customLinkType} ${getLinkVariant(typographyVariant)}`}
                        to={link}
                        target={isOpenAtNewPage ? "_blank" : "_self"}
                    >
                        {label}
                    </RouterLink>
                );
            }
        }

        return <span className={className}>{label}</span>;
    }, [
        className,
        disabled,
        download,
        isOpenAtNewPage,
        isOutside,
        label,
        link,
        onClick,
        styles.customLinkType,
        tabIndex,
        typographyVariant,
    ]);

    return (
        <Typography
            variant={typographyVariant}
            component="span"
            noWrap={noWrap}
            className={styles.wrapper}
            ref={reference}
        >
            {content}
        </Typography>
    );
}

export default Link;
